"use client";
import { Layout } from "@portal/app/_components/ui-elements/Layout";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DreamMarkdownReader } from "@portal/app/_components/ui-elements/DreamMarkdown/DreamMarkdownReader";
import { ShareSocials } from "../_components/ShareSocials";
import type { BlogPageProps } from "@portal/app/blog/[slug]/page";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { BlogContent } from "./_components/BlogContent";
import { DreamSearchSkeletonItem } from "@portal/app/health/_components/components/DreamSearchResult/DreamSearchSkeletonItem";
import {
  Carousel,
  CarouselSlider,
} from "@portal/app/health/_components/components/DreamSearchResult/DreamSearchResultsCarousel";
import { DreamSearchCarouselContainer } from "../_components/DreamSearchCarousel";
import { FormFunnelBanner } from "../_components/FormFunnelBanner";
import {
  ButtonSkeleton,
  TypographySkeleton,
} from "@portal/app/_components/ui-elements/Mui/DreamMuiSkeleton";
import { Suspense } from "react";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import { Dreamcrumbs } from "@portal/app/_components/ui-elements/Dreamcrumbs";
import { getUrls } from "@portal/app/_common/lib/urls";
import dreamboundStarLogo from "@portal/app/icon.svg";
import { splitBlogRichTextContent } from "../payloadRichText";
import { BlogRichTextReader } from "./_components/BlogRichTextReader";
import { BlogPostDate } from "../_components/BlogPostDate";
import { BLOG_ARTICLE_IMAGE_SIZES, getBlogImageProps } from "../blogImage";

export const BlogPage = ({
  initialGeoCenter,
  blogPost,
  variables,
  isPreview = false,
}: BlogPageProps) => {
  const analytics = useMixpanel();
  const { isMobile } = usePageContext();
  const urls = getUrls();
  const [contentTop, , contentBottom] = splitBlogRichTextContent(blogPost.content);
  const { authorDetails } = blogPost;
  const featuredImageUrl = blogPost.featuredImage?.url;
  void analytics.trackOnePageView("Blog - Viewed", {
    vocationId: blogPost.vocation?.id,
    industry: blogPost.industry,
    landingPage: blogPost.slug,
  });

  return (
    <Layout footer="BOTH">
      <BlogContainer>
        <BlogGrayBackdrop />
        <BlogContentContainer>
          {isPreview && (
            <PreviewBanner>
              <Typography variant="body2" fontWeight={700}>
                Preview mode
              </Typography>
              <PreviewExitLink
                href={`/api/preview/exit?return=${encodeURIComponent(urls.Blog(blogPost.slug))}`}
              >
                Exit preview
              </PreviewExitLink>
            </PreviewBanner>
          )}
          <Dreamcrumbs
            links={[
              { title: "Home", path: urls.Home() },
              { title: "Blog", path: urls.BlogHome() },
            ]}
            currentTitle={blogPost.title}
          />
          <article>
            <BlogHeaderSection>
              <BlogHeader data-testid="blog-page-title" variant="new_h3" component="h1">
                {blogPost.title}
              </BlogHeader>
              <BlogMeta>
                {authorDetails && <span>By {authorDetails.fullName}</span>}
                {authorDetails && <span aria-hidden="true">&middot;</span>}
                <span>
                  <BlogPostDate createdAt={blogPost.createdAt} updatedAt={blogPost.updatedAt} />
                </span>
              </BlogMeta>
            </BlogHeaderSection>
            {featuredImageUrl && (
              <BlogImageContainer>
                <BlogImage
                  {...getBlogImageProps(featuredImageUrl, {
                    sizes: BLOG_ARTICLE_IMAGE_SIZES,
                    intrinsicWidth: blogPost.featuredImage?.width,
                  })}
                  alt={blogPost.featuredImage?.altText || blogPost.title}
                  // Intrinsic dimensions let the browser reserve the right height
                  // before the image loads, so the fluid height costs no layout shift.
                  width={blogPost.featuredImage?.width ?? undefined}
                  height={blogPost.featuredImage?.height ?? undefined}
                  data-testid="blog-page-top-image"
                />
              </BlogImageContainer>
            )}
            <FormFunnelBanner
              vocationName={blogPost.vocation?.name}
              vocationId={blogPost.vocation?.id}
              industry={blogPost.industry}
            />
            {contentTop.root.children.length > 0 && (
              <BlogRichTextReader content={contentTop} data-testid="blog-page-content-top" />
            )}
            <Suspense
              fallback={
                <>
                  <TypographySkeleton sx={{ width: "60%", height: "1.2rem" }} />
                  <CarouselContainer>
                    <DreamSearchCarouselContainer sx={{ height: "406px" }}>
                      <Carousel px={2}>
                        <Box
                          sx={{
                            alignItems: "center",
                            pb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 1,
                              ...(!isMobile && {
                                width: "50rem",
                              }),
                            }}
                          >
                            <TypographySkeleton sx={{ width: "55%", height: "26px" }} />
                            <ButtonSkeleton sx={{ width: "92px" }} />
                          </Box>
                        </Box>
                        <CarouselSlider>
                          {[...Array(isMobile ? 2 : 6)].map((_e, idx) => (
                            <DreamSearchSkeletonItem key={idx} />
                          ))}
                        </CarouselSlider>
                      </Carousel>
                    </DreamSearchCarouselContainer>
                  </CarouselContainer>
                </>
              }
            >
              <BlogContent
                zip={blogPost.zip || initialGeoCenter.zip}
                industry={blogPost.industry}
                vocationId={blogPost.vocation?.id}
                vocationName={blogPost.vocation?.name}
                content={blogPost.content}
                variables={variables}
              />
            </Suspense>
            {!!contentBottom.root.children.length && (
              <BlogRichTextReader content={contentBottom} data-testid="blog-page-content-bottom" />
            )}
          </article>
          {authorDetails && (
            <BlogAbout>
              <AboutPortraitAndNameContainer>
                <AboutPortraitContainer>
                  {authorDetails.picture ? (
                    <BlogAuthorImage
                      src={authorDetails.picture}
                      alt={authorDetails.fullName}
                      data-testid="blog-page-author"
                    />
                  ) : (
                    <BlogAuthorImageFallback
                      aria-label={`${authorDetails.fullName} profile image placeholder`}
                      data-testid="blog-page-author"
                    >
                      <img src={dreamboundStarLogo.src} alt="" />
                    </BlogAuthorImageFallback>
                  )}
                </AboutPortraitContainer>
                <AuthorDetails>
                  <div>Written by</div>
                  <AuthorNameRow>
                    <address className="author_name">{authorDetails.fullName}</address>
                  </AuthorNameRow>
                </AuthorDetails>
              </AboutPortraitAndNameContainer>
              {authorDetails.bio ? <DreamMarkdownReader value={authorDetails.bio} /> : null}
            </BlogAbout>
          )}
          <BlogShareSocials>
            Share this post:
            <ShareSocials />
          </BlogShareSocials>
        </BlogContentContainer>
      </BlogContainer>
    </Layout>
  );
};

const BlogShareSocials = styled(Box)`
  display: flex;
  gap: 0.5em;
  flex-direction: column;
  align-self: flex-start;
`;
const PreviewBanner = styled(Box)`
  align-items: center;
  background: rgba(255, 223, 109, 0.18);
  border: 1px solid ${({ theme }) => theme.palette.dream.yellow};
  border-radius: 0.5rem;
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  width: 100%;
`;
const PreviewExitLink = styled("a")`
  color: ${({ theme }) => theme.palette.primary.main};
  font-weight: 700;
  text-decoration: underline;
`;
const BlogAbout = styled(Box)`
  padding: 1.25rem;
  background: rgba(250, 244, 244, 0.59);
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;
const AboutPortraitAndNameContainer = styled(Box)`
  display: flex;
  align-items: center;

  .author_name {
    font-weight: bold;
    margin: 0;
  }
`;
const AuthorDetails = styled(Box)`
  min-width: 0;
`;
const AuthorNameRow = styled(Box)`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
  min-width: 0;

  .author_name {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;
const AboutPortraitContainer = styled(Box)`
  margin-right: 1.25rem;
  border-radius: 50%;
  height: 6rem;
  width: 6rem;
  position: relative;
`;
const BlogContainer = styled(Box)`
  width: 100%;
  position: relative;
`;
const BlogContentContainer = styled(Box)`
  max-width: 50rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2.5rem 0;
  margin: auto;
  position: relative;

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: 1rem;
    gap: 1rem;
  }

  article {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;

    ${({ theme }) => theme.breakpoints.down("md")} {
      gap: 1rem;
    }
  }
`;
const CarouselContainer = styled(Box)`
  width: 100vw;
`;
const BlogGrayBackdrop = styled(Box)`
  background: ${({ theme }) => theme.palette.dream.grayLighter};
  position: absolute;
  top: 0;
  width: 100%;
  height: 300px;
`;
const BlogHeaderSection = styled("header")`
  width: 100%;
`;
const BlogHeader = styled(Typography)`
  font-size: 2rem;
  margin: -1px 0 0 0;
` as typeof Typography;
const BlogMeta = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.875rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;

  address {
    font-style: normal;
    display: inline;
  }
`;
const BlogImageContainer = styled(Box)`
  width: 100%;
  border-radius: 1rem;
  overflow: hidden;
`;

/**
 * Height tracks the image's aspect ratio so nothing is ever cropped, matching
 * the /view landing pages. The previous fixed 25rem height cropped from the
 * right (object-fit: cover anchored top left) at every width where the column
 * was narrower than the image's aspect ratio — most visibly between the "sm"
 * breakpoint and the 50rem column cap, which is exactly the tablet range.
 * Featured images routinely have title text baked into their left side, so
 * cropping silently swallowed content.
 *
 * The max-height guard only kicks in for unusually tall uploads; contain
 * letterboxes those instead of pushing the article far down the page.
 */
const BlogImage = styled("img")`
  display: block;
  width: 100%;
  height: auto;
  max-height: 25rem;
  object-fit: contain;
  color: transparent;
  background: ${({ theme }) => theme.palette.dream.grayLighter};
`;

const BlogAuthorImage = styled("img")`
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  object-fit: cover;
  object-position: center center;
  color: transparent;
`;

const BlogAuthorImageFallback = styled(Box)`
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #04299e;
  padding: 1rem;
  box-sizing: border-box;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;
